import { useId, useMemo, useRef, useState } from 'react';

export interface TrendSeries {
  key: string;
  label: string;
  color: string;
  /**
   * `null` = no sample for that point: the line BREAKS there and, where every series is null, the
   * hatched gap band is drawn — a missing sample never reads as zero (CP3).
   */
  points: { date: string; value: number | null }[];
}

interface TrendChartProps {
  series: TrendSeries[];
  height?: number;
  /** Dates with no ingested data — drawn as a hatched band so a gap never reads as zero. */
  missingDates?: string[];
  /** X label for a point key. Default: a `yyyy-MM-dd` day → `26 Sep`. Timestamps pass their own. */
  formatX?: (key: string) => string;
  /** Y ticks, end labels and tooltip values. Default: {@link formatCompact}. */
  formatValue?: (value: number) => string;
  /** Tooltip wording for a null point. */
  gapLabel?: string;
  /**
   * The axis ceiling for a max value. Default: 1/2/5 × 10ⁿ. Bytes pass a 1024-based one so the
   * ticks read 512 MB / 1 GB rather than 476.8 MB / 953.7 MB.
   */
  niceMax?: (max: number) => number;
}

const PAD = { top: 16, right: 56, bottom: 28, left: 48 };
const VIEW_W = 900;

/**
 * Approximate advance of one 11-unit label character. Only used to WIDEN the default padding for a
 * custom formatter (bytes, milliseconds, percent) whose labels are longer than the compact counts the
 * default padding was sized for — a clipped "476.8 MB" reads as "6.8 MB", which is worse than no label.
 */
const LABEL_CHAR = 6.6;

/**
 * Time-series line chart.
 *
 * Follows the mark spec: 2px lines with round joins, >=8px end markers carrying a 2px
 * surface ring, hairline SOLID gridlines (never dashed), a ~10% area wash for a single
 * series, and selective direct labels (endpoint only — never a number on every point).
 *
 * Deliberately hand-rolled SVG: it is ~150 lines, has no dependency, and lets the specs
 * be followed exactly rather than fought against in a library's theming API.
 */
export function TrendChart({
  series,
  height = 260,
  missingDates = [],
  formatX = formatDay,
  formatValue = formatCompact,
  gapLabel = 'no sample',
  niceMax = niceCeiling,
}: TrendChartProps) {
  const clipId = useId();
  const wrapRef = useRef<HTMLDivElement>(null);
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);

  const dates = useMemo(() => series[0]?.points.map((p) => p.date) ?? [], [series]);

  const max = useMemo(() => {
    const values = series.flatMap((s) => s.points.map((p) => p.value)).filter((v): v is number => v !== null);
    return values.length ? Math.max(...values) : 0;
  }, [series]);

  // Round the ceiling to a clean number so ticks read 0 / 1,000 / 2,000.
  const ceiling = useMemo(() => niceMax(max), [max, niceMax]);
  const ticks = useMemo(() => [0, 0.25, 0.5, 0.75, 1].map((f) => Math.round(ceiling * f)), [ceiling]);

  // The default padding fits the default compact labels; a custom formatter gets room for its own.
  // Existing callers (no formatter) keep the exact geometry they had.
  const custom = formatValue !== formatCompact;
  const padLeft = custom
    ? Math.max(PAD.left, Math.ceil(Math.max(...ticks.map((t) => formatValue(t).length)) * LABEL_CHAR) + 14)
    : PAD.left;
  const endLabelChars = custom
    ? Math.max(0, ...series.map((s) => {
      const i = lastMeasured(s.points);
      const v = i >= 0 ? s.points[i]!.value : null;
      return v === null ? 0 : formatValue(v).length;
    }))
    : 0;
  const padRight = custom ? Math.max(PAD.right, Math.ceil(endLabelChars * LABEL_CHAR) + 18) : PAD.right;

  const plotW = VIEW_W - padLeft - padRight;
  const plotH = height - PAD.top - PAD.bottom;

  const x = (i: number) => (dates.length <= 1 ? padLeft + plotW / 2 : padLeft + (i / (dates.length - 1)) * plotW);
  const y = (v: number) => PAD.top + plotH - (ceiling === 0 ? 0 : (v / ceiling) * plotH);

  if (dates.length === 0) {
    return <div className="empty-state t-body">No data in this range yet.</div>;
  }

  // A point where EVERY series has no sample is a gap in the chart as a whole, drawn like a missing day.
  const missing = new Set(missingDates);
  dates.forEach((d, i) => {
    if (series.every((s) => s.points[i]?.value === null)) missing.add(d);
  });
  const hoveredDate = hoverIndex === null ? null : dates[hoverIndex];

  return (
    <div className="chart-wrap" ref={wrapRef}>
      {series.length >= 2 && (
        <div className="legend" style={{ marginBottom: 12 }}>
          {series.map((s) => (
            <span key={s.key} className="legend-item">
              <span className="legend-key" style={{ background: s.color }} aria-hidden="true" />
              {s.label}
            </span>
          ))}
        </div>
      )}

      <svg
        className="chart-svg"
        viewBox={`0 0 ${VIEW_W} ${height}`}
        role="img"
        aria-label={`Trend of ${series.map((s) => s.label).join(', ')}`}
        onMouseLeave={() => setHoverIndex(null)}
        onMouseMove={(event) => {
          const rect = event.currentTarget.getBoundingClientRect();
          const px = ((event.clientX - rect.left) / rect.width) * VIEW_W;
          const ratio = (px - padLeft) / plotW;
          const index = Math.round(ratio * (dates.length - 1));
          setHoverIndex(Math.min(dates.length - 1, Math.max(0, index)));
        }}
      >
        <defs>
          <clipPath id={clipId}>
            <rect x={padLeft} y={PAD.top} width={plotW} height={plotH} />
          </clipPath>
          <pattern id={`${clipId}-gap`} width="6" height="6" patternTransform="rotate(45)" patternUnits="userSpaceOnUse">
            <rect width="6" height="6" fill="var(--bg-main)" />
            <line x1="0" y1="0" x2="0" y2="6" stroke="var(--viz-grid)" strokeWidth="2" />
          </pattern>
        </defs>

        {/* Gridlines — hairline, solid, recessive. */}
        {ticks.map((t) => (
          <g key={t}>
            <line x1={padLeft} x2={padLeft + plotW} y1={y(t)} y2={y(t)} stroke="var(--viz-grid)" strokeWidth="1" />
            <text
              x={padLeft - 10}
              y={y(t) + 4}
              textAnchor="end"
              fontSize="11"
              fill="var(--viz-muted)"
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {formatValue(t)}
            </text>
          </g>
        ))}

        {/* Missing days — hatched, so a gap can never be misread as a real zero. */}
        {/*
          CLIPPED and CLAMPED, both deliberately. The band is centred on its day, so the first and
          last days put half a band outside the plot: with two dates the band is the full plot width
          and the first rect starts 350px LEFT of the SVG origin. Unclipped (and with the SVG's
          overflow visible) that painted a grey slab across the card and over the sidebar.
          Clamping keeps the geometry honest — a half-band at the edge represents half a day — and
          the clipPath is the backstop so no future arithmetic slip can escape the plot again.
        */}
        {dates.map((d, i) => {
          if (!missing.has(d)) return null;

          const band = plotW / Math.max(1, dates.length - 1);
          const rawX = x(i) - band / 2;
          const left = Math.max(padLeft, rawX);
          const right = Math.min(padLeft + plotW, rawX + band);

          // A band entirely outside the plot has nothing to say.
          if (right <= left) return null;

          return (
            <rect
              key={`gap-${d}`}
              x={left}
              y={PAD.top}
              width={right - left}
              height={plotH}
              fill={`url(#${clipId}-gap)`}
              opacity={0.7}
              clipPath={`url(#${clipId})`}
            />
          );
        })}

        {/* Area wash — only when a single series is plotted; one wash per unbroken run. */}
        {series.length === 1 && series[0] &&
          runs(series[0].points).map((run, r) => (
            <path
              key={`area-${r}`}
              d={areaPath(run.map(({ i, v }) => [x(i), y(v)] as const), PAD.top + plotH)}
              fill={series[0]!.color}
              opacity={0.1}
              clipPath={`url(#${clipId})`}
            />
          ))}

        {/* The line BREAKS at a null point: joining across it would draw a value nobody measured. */}
        {series.map((s) =>
          runs(s.points).map((run, r) =>
            run.length === 1 ? (
              // An isolated sample between gaps has no neighbour to join — a small dot keeps it visible.
              <circle
                key={`${s.key}-dot-${r}`}
                cx={x(run[0]!.i)}
                cy={y(run[0]!.v)}
                r="2.5"
                fill={s.color}
                clipPath={`url(#${clipId})`}
              />
            ) : (
              <path
                key={`${s.key}-${r}`}
                d={linePath(run.map(({ i, v }) => [x(i), y(v)] as const))}
                fill="none"
                stroke={s.color}
                strokeWidth="2"
                strokeLinejoin="round"
                strokeLinecap="round"
                clipPath={`url(#${clipId})`}
              />
            ),
          ),
        )}

        {/* Crosshair */}
        {hoverIndex !== null && (
          <line
            x1={x(hoverIndex)}
            x2={x(hoverIndex)}
            y1={PAD.top}
            y2={PAD.top + plotH}
            stroke="var(--viz-axis)"
            strokeWidth="1"
          />
        )}

        {/* End markers + hovered markers: r>=4 with a 2px surface ring. The end is the last MEASURED
            point — a trailing gap does not drag the marker down to a value that was never sampled. */}
        {series.map((s) => {
          const lastIndex = lastMeasured(s.points);
          const last = lastIndex >= 0 ? s.points[lastIndex] : undefined;
          const hovered = hoverIndex !== null ? s.points[hoverIndex] : undefined;
          return (
            <g key={`${s.key}-markers`}>
              {last && last.value !== null && (
                <circle cx={x(lastIndex)} cy={y(last.value)} r="4" fill={s.color} stroke="var(--viz-surface)" strokeWidth="2" />
              )}
              {hovered && hovered.value !== null && hoverIndex !== lastIndex && (
                <circle cx={x(hoverIndex!)} cy={y(hovered.value)} r="4" fill={s.color} stroke="var(--viz-surface)" strokeWidth="2" />
              )}
            </g>
          );
        })}

        {/* Selective direct label: the endpoint only. Text wears an ink token, never the series colour. */}
        {series.map((s) => {
          const lastIndex = lastMeasured(s.points);
          const last = lastIndex >= 0 ? s.points[lastIndex] : undefined;
          if (!last || last.value === null) return null;
          return (
            <text
              key={`${s.key}-label`}
              x={x(lastIndex) + 10}
              y={y(last.value) + 4}
              fontSize="11"
              fontWeight="600"
              fill="var(--text-main)"
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {formatValue(last.value)}
            </text>
          );
        })}

        {/* X ticks — first, middle, last only; a tick per day collides. */}
        {[0, Math.floor((dates.length - 1) / 2), dates.length - 1]
          .filter((v, i, arr) => arr.indexOf(v) === i && dates[v])
          .map((i) => (
            <text key={`x-${i}`} x={x(i)} y={height - 8} textAnchor="middle" fontSize="11" fill="var(--viz-muted)">
              {formatX(dates[i]!)}
            </text>
          ))}

        <line
          x1={padLeft}
          x2={padLeft + plotW}
          y1={PAD.top + plotH}
          y2={PAD.top + plotH}
          stroke="var(--viz-axis)"
          strokeWidth="1"
        />
      </svg>

      {hoverIndex !== null && hoveredDate && (
        <div
          className="tooltip"
          style={{ left: `${(x(hoverIndex) / VIEW_W) * 100}%`, top: PAD.top }}
          role="status"
        >
          <div className="t-label" style={{ marginBottom: 4, color: 'var(--text-muted)' }}>
            {formatX(hoveredDate)}
            {missing.has(hoveredDate) ? ` · ${missingDates.includes(hoveredDate) ? 'not ingested' : gapLabel}` : ''}
          </div>
          {series.map((s) => {
            const value = s.points[hoverIndex]?.value;
            return (
              <div className="tooltip-row" key={s.key}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <span className="tooltip-dot" style={{ background: s.color }} aria-hidden="true" />
                  {s.label}
                </span>
                {/* A null is SAID to be missing — the old `?? 0` would have printed a zero here. */}
                <strong className="tabular">
                  {value === null ? gapLabel : formatValue(value ?? 0)}
                </strong>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** Consecutive runs of measured points, each with its original index — the line is drawn per run. */
function runs(points: { value: number | null }[]): { i: number; v: number }[][] {
  const out: { i: number; v: number }[][] = [];
  let current: { i: number; v: number }[] = [];
  points.forEach((p, i) => {
    if (p.value === null) {
      if (current.length) out.push(current);
      current = [];
    } else {
      current.push({ i, v: p.value });
    }
  });
  if (current.length) out.push(current);
  return out;
}

function lastMeasured(points: { value: number | null }[]): number {
  for (let i = points.length - 1; i >= 0; i--) {
    if (points[i]!.value !== null) return i;
  }
  return -1;
}

function linePath(points: readonly (readonly [number, number])[]): string {
  return points.map(([px, py], i) => `${i === 0 ? 'M' : 'L'}${px.toFixed(2)},${py.toFixed(2)}`).join(' ');
}

function areaPath(points: readonly (readonly [number, number])[], baseline: number): string {
  if (points.length === 0) return '';
  const first = points[0]!;
  const last = points[points.length - 1]!;
  return `${linePath(points)} L${last[0].toFixed(2)},${baseline} L${first[0].toFixed(2)},${baseline} Z`;
}

/** Rounds up to a clean axis ceiling (1/2/5 × 10ⁿ) so ticks land on readable numbers. */
function niceCeiling(max: number): number {
  if (max <= 0) return 10;
  const magnitude = 10 ** Math.floor(Math.log10(max));
  const normalised = max / magnitude;
  const step = normalised <= 1 ? 1 : normalised <= 2 ? 2 : normalised <= 5 ? 5 : 10;
  return step * magnitude;
}

export function formatCompact(value: number): string {
  if (!Number.isFinite(value)) return '—';
  if (Math.abs(value) >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (Math.abs(value) >= 10_000) return `${(value / 1_000).toFixed(1)}K`;
  return Math.round(value).toLocaleString('en-US');
}

function formatDay(iso: string): string {
  const date = new Date(`${iso}T00:00:00Z`);
  return Number.isNaN(date.getTime())
    ? iso
    : date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
}
